import { buildTOCTree, extractTOC, transformToAnchorItems, TableOfContent } from '../TableOfContent/TableOfContent';
import { Remirror, useRemirror, useHelpers, useCommands, EditorComponent, useRemirrorContext } from '@remirror/react';
import React, { forwardRef, useImperativeHandle, useState, useCallback, useRef, useMemo } from 'react';
import { transactionManager } from '../../core/txn/TransactionManager';
import CmsFindReplace from '../find-replace/FindReplaceComponent';
import { extensionsList } from '../../utils/editor-extensions';
import { FloatingToolbar } from './../FloatingToolbar';
import CodeBlockComponent from './CodeBlockComponent';
import { TextSelection, Selection } from '@remirror/pm/state';
import { ThemeProvider } from './ThemeProvider';
import TableComponents from './TableComponent';
import { useTransaction } from '../../hooks';
import { Step } from 'prosemirror-transform';
import { ShortcutHOC } from './ShortcutHOC';
import EditableCard from './EditableCard';
import 'editor/src/styles/global.css';
import 'remirror/styles/all.css';
import { Flex } from 'antd';

const isValidJSON = (str = '') => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};
export const CmsEditor = ({
  isEditable = false,
  editorContent = '',
  editorRef,
  file_id,
  workspace_id,
  organization_id,
  isGitBasedWorkspace,
  metaData,
  theme = localStorage.getItem('mainTheme') || 'light',
  updateFilename,
  user,
  onChange,
  disableRichFeatures = false,
  container,
  className,
  disableTableOfContent = false,
  onTransactionChange,
  autofocus = false,
}) => {
  let stringHandler = 'markdown';
  const isJSON = editorContent ? isValidJSON(editorContent?.data) : false;
  if (editorContent && isJSON) stringHandler = 'json';
  const extensions = useMemo(
    () => extensionsList({ isEditable, theme, file_id, workspace_id, organization_id, user, disableRichFeatures }),
    [isEditable, theme, file_id, workspace_id, organization_id, user, disableRichFeatures]
  );
  const parsedContent = useMemo(() => {
    if (!editorContent) return undefined;
    return isJSON ? JSON.parse(editorContent) : editorContent?.data;
  }, [editorContent, isJSON]);

  const { manager, state } = useRemirror({
    extensions,
    content: parsedContent,
    selection: 'start',
    stringHandler,
  });

  const { dispatchTransaction } = useTransaction({ onTransactionChange });
  const [paginationContent, setPaginationContent] = useState(
    transformToAnchorItems(buildTOCTree(extractTOC(editorContent)))
  );

  const renderingRef = useRef(true);

  const SaveContent = forwardRef((_, ref) => {
    const { getMarkdown, getJSON } = useHelpers();
    const { setContent } = useCommands();
    const rmEditor = useRemirrorContext();

    useImperativeHandle(ref, () => ({
      getMarkdown,
      setContent,
      getJSON,
      applyTransaction: ({ content, transactions }) => {
        setContent(content);
        if (transactions?.length > 0) {
          const steps = transactions.map((stepJson) => Step.fromJSON(manager.schema, stepJson));
          const baseState = rmEditor.getState();
          let tr = baseState.tr;
          for (const step of steps) {
            const result = tr.step(step);
            if (result.failed) {
              console.warn('Failed to apply step:', result.failed);
            }
          }
          rmEditor.view.dispatch(tr);
        }
        renderingRef.current = false;
      },
    }));
    return null;
  });

  const handleEnterPress = () => {
    const { view } = manager;
    view.focus();
    const startPos = 0;
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, startPos)));
  };

  const handleChange = useCallback(
    ({ helpers }) => {
      const content = helpers.getJSON();
      onChange?.(content);
      if (!isGitBasedWorkspace && !disableRichFeatures) {
        setPaginationContent(transformToAnchorItems(buildTOCTree(extractTOC(content))));
      }
    },
    [isGitBasedWorkspace]
  );

  const handleTransaction = useCallback(
    (tr, state) => {
      const trChanged = renderingRef.current === false && tr.steps.length > 0 && !tr?.meta?.pointer;
      if (isEditable && !isGitBasedWorkspace && trChanged) {
        const isDocChanged = JSON.stringify(tr.doc.toJSON()) !== JSON.stringify(state.doc.toJSON());
        if (isDocChanged) {
          const data = transactionManager.toDBJson(tr);
          dispatchTransaction(data);
        }
      }
    },
    [isEditable, isGitBasedWorkspace, dispatchTransaction, renderingRef.current]
  );

  return (
    <div
      className={className}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <div className={`remirror-theme ${isEditable ? 'edit' : 'non-edit-mode'}`}>
          <ThemeProvider>
            <Remirror
              onChange={handleChange}
              onDispatchTransaction={handleTransaction}
              manager={manager}
              initialContent={state}
              editable={isEditable}
              autoFocus={isEditable && autofocus}
            >
              <Flex style={{ position: 'relative', width: '100%' }} gap={48}>
                <Flex style={{ width: '100%' }}>
                  <SaveContent ref={editorRef} />
                  {!disableRichFeatures && <FloatingToolbar isEditable={isEditable} fileId={file_id} />}

                  {isEditable && !disableRichFeatures && (
                    <>
                      <TableComponents theme={theme} />
                      <CodeBlockComponent />
                      <CmsFindReplace isEditable={isEditable} />
                    </>
                  )}
                  <ShortcutHOC
                    editable={isEditable}
                    fileId={file_id}
                    workspaceId={workspace_id}
                    organizationId={organization_id}
                    disableRichFeatures={disableRichFeatures}
                  />

                  <div className={isEditable ? 'edit-editor-title' : 'editor-title'}>
                    {!isGitBasedWorkspace && metaData?.workspaceTitle && !disableRichFeatures && (
                      <EditableCard
                        metaData={metaData}
                        isEditable={isEditable}
                        updateFilename={updateFilename}
                        onEnterPress={handleEnterPress}
                      />
                    )}

                    <EditorComponent />
                  </div>
                </Flex>
              </Flex>
            </Remirror>
          </ThemeProvider>
        </div>
      </div>
      {!disableRichFeatures && !disableTableOfContent && (
        <TableOfContent content={paginationContent} container={container} />
      )}
    </div>
  );
};
